import { Box, Flex } from '@chakra-ui/react';
import { useRouterState } from '@tanstack/react-router';

import { DiscordAdminFooter } from '#features/discord-lfg-admin/components/DiscordAdminFooter';
import { DiscordAdminHeader } from '#features/discord-lfg-admin/components/DiscordAdminHeader';
import { AppFooter } from '#features/layout/components/AppFooter';
import { VOICE_BAR_RESERVE } from '#features/media-window/model';
import { BottomNavigation } from '../BottomNavigation';
import { Header } from '../Header';

type MainLayoutProps = {
  children: React.ReactNode;
  /**
   * Обычная страница сайта, что бы ни лежало в совпавших маршрутах.
   *
   * Нужно экрану «страницы нет»: путь вроде `/nope` остаётся совпавшим с лендингом, и тот
   * забирает себе и шапку, и подвал — лендинг рисует свой собственный, поэтому глобальный
   * подавляется, и страница ошибки оставалась без подвала и с маркетинговой навигацией.
   */
  plain?: boolean;
};

export const MainLayout = ({ children, plain = false }: MainLayoutProps) => {
  const isLandingMatch = useRouterState({
    select: (state) =>
      state.matches?.some((match) => match?.routeId?.includes('/_landing')) ?? false,
  });
  const isLandingPage = !plain && isLandingMatch;

  /**
   * Экран переписки, а не адрес переписки.
   *
   * Тред открывается двумя путями: по своему адресу `/chats/$chatId` и по `?peerUserId=` из
   * списков людей — кнопкой «Написать». Второй путь остаётся на индексном маршруте `/chats`,
   * и раньше сюда не попадал: страница сохраняла `minH: 100vh` и обычный скролл, поэтому на
   * телефоне переписка листалась как лента — от самых старых сообщений вниз, вместо того чтобы
   * стоять на месте и открываться на последнем, как ждут от мессенджера.
   */
  const isChatThread = useRouterState({
    select: (state) =>
      state.matches.some(
        (match) =>
          match.routeId.includes('/chats/$chatId') ||
          (match.routeId.includes('/dashboard/chats/') &&
            Boolean((match.search as { peerUserId?: string } | undefined)?.peerUserId))
      ),
  });

  // The Discord server-owner admin gets its own slim header (logo + account) instead of
  // the consumer teamplay.gg one — the app nav (notifications, create post, dashboard
  // settings) is irrelevant there. Matched by routeId like isLandingPage/isChatThread.
  const isDiscordAdmin = useRouterState({
    select: (state) =>
      state.matches?.some((match) => match?.routeId?.includes('/discord-lfg-bot/admin')) ?? false,
  });

  // The landing renders its own footer inside its content grid (so it inherits the
  // landing-scoped width + contrast overrides), so the global footer is suppressed
  // there to avoid a duplicate. Driven by `staticData.rendersOwnFooter` on the route
  // (not a path match), so it keeps working when the landing moves to prod routes.
  const ownFooterMatch = useRouterState({
    select: (state) => state.matches?.some((match) => match?.staticData?.rendersOwnFooter) ?? false,
  });
  const rendersOwnFooter = !plain && ownFooterMatch;

  // The LFG bot landing renders its own branded header component, so it flags
  // `staticData.rendersOwnHeader` to suppress the global one entirely (no stacked headers).
  const ownHeaderMatch = useRouterState({
    select: (state) => state.matches?.some((match) => match?.staticData?.rendersOwnHeader) ?? false,
  });
  const rendersOwnHeader = !plain && ownHeaderMatch;

  // Pages with their own marketing chrome (rendersOwnHeader) also hide the app bottom
  // nav: they carry their own sticky mobile CTA, which would overlap/cover it.
  const hideBottomNav = isLandingPage || isChatThread || isDiscordAdmin || rendersOwnHeader;

  return (
    <Flex
      w="100%"
      minH={{ base: isChatThread ? 0 : '100vh', lg: '100vh' }}
      position={{ base: isChatThread ? 'fixed' : 'static', lg: 'static' }}
      inset={{ base: isChatThread ? 0 : 'auto', lg: 'auto' }}
      overflow={{ base: isChatThread ? 'hidden' : 'visible', lg: 'visible' }}
      flexDir="column"
      m="0 auto"
    >
      {!rendersOwnHeader && (isDiscordAdmin ? <DiscordAdminHeader /> : <Header />)}
      <Flex
        as="main"
        alignItems="stretch"
        position="relative"
        flexDirection="column"
        flex="1"
        minH={0}
        // Полоса разговора занимает низ экрана наравне с навигацией, поэтому и место под неё
        // страница оставляет так же — иначе она ложится на то, что оказалось внизу: кнопку,
        // последнюю карточку ленты, футер формы.
        //
        // Именно `max`, а не сумма: полоса стоит НАД навигацией и в занятое ею место навигацию уже
        // включает. Сложение отсчитало бы навигацию и безопасную зону дважды и оторвало бы страницу
        // от нижнего края на высоту меню.
        pb={{
          base: hideBottomNav
            ? VOICE_BAR_RESERVE
            : `max(calc(env(safe-area-inset-bottom) + 72px), ${VOICE_BAR_RESERVE})`,
          md: 0,
        }}
      >
        <Flex
          px={{ base: isChatThread ? 0 : 3, lg: 5 }}
          py={{ base: isChatThread ? 0 : 5, lg: 5 }}
          mx="auto"
          mb={{ base: isChatThread ? 0 : 'auto', lg: 'auto' }}
          w="100%"
          maxW="1800px"
          minH={{ base: isChatThread ? 0 : 'calc(100vh - 80px)', lg: 'calc(100vh - 80px)' }}
          display="flex"
          flexDir="column"
          alignItems="center"
          flex="1"
          overflowY={{ base: isChatThread ? 'hidden' : 'visible', lg: 'visible' }}
          overflowX="clip"
        >
          {children}
        </Flex>
        {isDiscordAdmin ? (
          <DiscordAdminFooter />
        ) : (
          !rendersOwnFooter && (
            <Box
              display={{ base: isChatThread ? 'none' : 'block', lg: 'block' }}
              // Позиционирование здесь не ради смещения, а ради порядка отрисовки: страницы
              // дашборда полны sticky-блоков (левое меню, календарь сессий), а позиционированное
              // всегда рисуется поверх обычного потока — независимо от порядка в разметке. Без
              // этой строки любой sticky-сосед ложится на футер, стоит странице стать длиннее.
              position="relative"
              w="100%"
              maxW={{ base: '100%', md: '1200px' }}
              mx="auto"
              px={{ base: 4, md: 6, lg: 8 }}
            >
              <AppFooter />
            </Box>
          )
        )}
      </Flex>
      {!hideBottomNav && <BottomNavigation />}
    </Flex>
  );
};
